import { useState } from 'react';
import { BarChart } from '../../charts/BarChart';
import { BarList, ChartCard, Drawer, Stat } from '../../components/data';
import { Empty, ErrorBanner, Field, Loading } from '../../components/ui';
import { downloadCsv, fileStamp, slugForFile } from '../../lib/download';
import { formatNumber, num, periodLabel, toLocalDateString, todayLocal } from '../../lib/format';
import { useHeldResource, useResource } from '../../lib/hooks';

interface Activity {
  found: boolean;
  name: string;
  email: string | null;
  autotaskName?: string;
  from?: string;
  to?: string;
  totals?: {
    hours: number;
    billable: number;
    ticketHours: number;
    projectHours: number;
    otherHours: number;
    entries: number;
    ticketsCompleted: number;
    avgHoursPerTicket: number | null;
    openTickets: number;
    surveys: number;
    avgResourceRating: number | null;
    avgSurveyRating: number | null;
    projectsLeadingOpen: number;
    projectsCompleted: number;
    tasksCompleted: number;
  };
  months?: { month: string; hours: number; billable: number; ticketHours: number; projectHours: number; otherHours: number; ticketsCompleted: number }[];
  clients?: { name: string; hours: number }[];
  issueTypes?: { name: string; count: number }[];
  projects?: { id?: number; name: string; status: string; client: string | null; end: string | null }[];
  fetchedUtc?: string;
  sources?: { figure: string; entity: string; fields: string; filter: string; formula: string; records: number }[];
}

/* The time frame: quick picks, or any from/to. */
function quick(k: '12m' | 'lastWeek' | 'month' | 'lastMonth' | 'quarter' | 'lastQuarter' | 'ytd' | 'lastYear'): { from: string; to: string } {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  const q = Math.floor(m / 3);
  const ld = toLocalDateString;
  switch (k) {
    case 'lastWeek': {
      // Monday to Sunday of the week before this one.
      const monday = d.getDate() - ((d.getDay() + 6) % 7);
      return { from: ld(new Date(y, m, monday - 7)), to: ld(new Date(y, m, monday - 1)) };
    }
    case 'month':
      return { from: ld(new Date(y, m, 1)), to: todayLocal() };
    case 'lastMonth':
      return { from: ld(new Date(y, m - 1, 1)), to: ld(new Date(y, m, 0)) };
    case 'quarter':
      return { from: ld(new Date(y, q * 3, 1)), to: todayLocal() };
    case 'lastQuarter':
      return { from: ld(new Date(y, (q - 1) * 3, 1)), to: ld(new Date(y, q * 3, 0)) };
    case 'ytd':
      return { from: ld(new Date(y, 0, 1)), to: todayLocal() };
    case 'lastYear':
      return { from: ld(new Date(y - 1, 0, 1)), to: ld(new Date(y - 1, 11, 31)) };
    default:
      return { from: ld(new Date(y, m - 11, 1)), to: ld(new Date(y, m + 1, 0)) };
  }
}
const QUICK: { k: Parameters<typeof quick>[0]; label: string }[] = [
  { k: '12m', label: 'Last 12 months' },
  { k: 'lastWeek', label: 'Last week' },
  { k: 'month', label: 'This month' },
  { k: 'lastMonth', label: 'Last month' },
  { k: 'quarter', label: 'This quarter' },
  { k: 'lastQuarter', label: 'Last quarter' },
  { k: 'ytd', label: 'This year' },
  { k: 'lastYear', label: 'Last year' },
];

const h = (v: number) => `${formatNumber(Math.round(v * 10) / 10)} h`;
const pct = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)}%` : '—');
const day = (iso: string | null | undefined) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/**
 * People › a person's Activity: their last twelve months in Autotask in one
 * view — the time they logged and where it went, the tickets they closed and
 * how clients rated them, the projects they lead and the tasks they finished.
 * Read from Autotask (routes/peopleActivity.ts), matched by email, else name;
 * held until Refresh. Every figure opens the records behind it (DrillDrawer).
 */
export function PersonActivity({ employeeId }: { employeeId: number }) {
  const [{ from, to }, setRange] = useState(() => quick('12m'));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };
  const data = useHeldResource<Activity>(`/people/directory/${employeeId}/activity?${new URLSearchParams({ from, to })}`);
  const d = data.data;
  const [drill, setDrill] = useState<Drill | null>(null);
  const open = (x: Drill) => setDrill(x);

  const rangeBar = (
    <div className="row wrap mt-4 mb-2" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
      <Field label="From">
        <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
      </Field>
      <Field label="To">
        <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
      </Field>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
        onClick={() => setRange({ from: draftFrom, to: draftTo })}
      >
        Show
      </button>
      <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
        {QUICK.map((q) => (
          <button key={q.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quick(q.k))}>
            {q.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (data.loading && !d)
    return (
      <>
        {rangeBar}
        <Loading label="Reading their work from Autotask" />
      </>
    );
  if (data.error)
    return (
      <>
        {rangeBar}
        <ErrorBanner message={data.error} />
      </>
    );
  if (!d) return null;
  if (!d.found || !d.totals) {
    return (
      <Empty title="Not found in Autotask">
        <p>
          No Autotask resource has {d.email ? `the email ${d.email}` : 'their email'} or the name {d.name}, so there is no Autotask
          activity to show. Their email here and in Autotask need to match.
        </p>
      </Empty>
    );
  }
  const t = d.totals;
  const months = d.months ?? [];

  return (
    <div>
      {rangeBar}
      <div className="row-between wrap mb-2">
        <p className="xs muted">
          {d.autotaskName} in Autotask · {day(d.from)} – {day(d.to)}
          {data.fetchedAt &&
            ` · as of ${new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
        </p>
        <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
          {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
        </button>
      </div>

      <div className="grid grid-4 mb-4 pa-tiles">
        <Tile onClick={() => open({ kind: 'time', title: 'All time logged' })}>
          <Stat label="Hours logged" value={h(t.hours)} foot={`${num(t.entries)} time entries`} />
        </Tile>
        <Tile onClick={() => open({ kind: 'time', category: 'billable', title: 'Billable time' })}>
          <Stat label="Billable" value={h(t.billable)} foot={`${pct(t.billable, t.hours)} of hours logged`} />
        </Tile>
        <Tile onClick={() => open({ kind: 'completed', title: 'Tickets completed' })}>
          <Stat
            label="Tickets completed"
            value={num(t.ticketsCompleted)}
            foot={t.avgHoursPerTicket === null ? 'in this period' : `${t.avgHoursPerTicket} h each on average`}
          />
        </Tile>
        <Tile onClick={() => open({ kind: 'surveys', title: 'Survey responses on their tickets' })}>
          <Stat
            label="Client rating"
            value={t.avgResourceRating === null ? '—' : `${t.avgResourceRating.toFixed(2)} / 5`}
            foot={t.surveys ? `${num(t.surveys)} survey${t.surveys === 1 ? '' : 's'} · survey avg ${t.avgSurveyRating?.toFixed(2) ?? '—'}` : 'no survey responses'}
          />
        </Tile>
        <Tile onClick={() => open({ kind: 'time', category: 'ticket', title: 'Ticket time' })}>
          <Stat label="Ticket time" value={h(t.ticketHours)} foot={pct(t.ticketHours, t.hours)} />
        </Tile>
        <Tile onClick={() => open({ kind: 'time', category: 'project', title: 'Project time' })}>
          <Stat label="Project time" value={h(t.projectHours)} foot={pct(t.projectHours, t.hours)} />
        </Tile>
        <Tile onClick={() => open({ kind: 'time', category: 'other', title: 'Other time' })}>
          <Stat label="Other time" value={h(t.otherHours)} foot={pct(t.otherHours, t.hours)} />
        </Tile>
        <Tile onClick={() => open({ kind: 'open', title: 'Tickets open on them now' })}>
          <Stat label="Open tickets" value={num(t.openTickets)} foot="on them now" />
        </Tile>
        <Tile onClick={() => open({ kind: 'projects', title: 'Projects they lead' })}>
          <Stat label="Projects led" value={num(t.projectsLeadingOpen)} foot={`open now · ${num(t.projectsCompleted)} completed in this period`} />
        </Tile>
        <Tile onClick={() => open({ kind: 'tasks', title: 'Project tasks completed' })}>
          <Stat label="Tasks completed" value={num(t.tasksCompleted)} foot="project tasks, this period" />
        </Tile>
      </div>

      <ChartCard className="mb-4" title="Hours by month" subtitle="Ticket, project and other time.">
        <BarChart
          data={months}
          xKey="month"
          stacked
          series={[
            { key: 'ticketHours', label: 'Tickets', color: 'var(--series-1)' },
            { key: 'projectHours', label: 'Projects', color: 'var(--series-2)' },
            { key: 'otherHours', label: 'Other', color: 'var(--series-4)' },
          ]}
          xFormat={periodLabel}
          yFormat={(v) => num(v, 0)}
          height={220}
          emptyMessage="No time logged in the last year."
        />
      </ChartCard>

      <div className="grid grid-2 mb-4">
        <ChartCard title="Where the hours went" subtitle="The ten clients they logged the most time for. Click one for its entries.">
          <div className="pa-pick">
            {(d.clients ?? []).map((c) => (
              <button key={c.name} type="button" onClick={() => open({ kind: 'time', client: c.name, title: `Time for ${c.name}` })}>
                <BarList rows={[{ name: c.name, value: c.hours, display: h(c.hours) }]} />
              </button>
            ))}
          </div>
        </ChartCard>
        <ChartCard title="Tickets completed, by issue type" subtitle="Click one for its tickets.">
          <div className="pa-pick">
            {(d.issueTypes ?? []).map((c) => (
              <button key={c.name} type="button" onClick={() => open({ kind: 'completed', issueType: c.name, title: `Tickets completed — ${c.name}` })}>
                <BarList rows={[{ name: c.name, value: c.count, display: num(c.count) }]} />
              </button>
            ))}
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-2">
        <section className="card">
          <div className="card-head">
            <div className="grow">
              <h3>Month by month</h3>
              <p className="sub">Click a figure for the entries or tickets behind it.</p>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="num">Hours</th>
                  <th className="num">Billable</th>
                  <th className="num">Tickets completed</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <tr key={m.month}>
                    <td>{periodLabel(m.month)}</td>
                    <td className="num">
                      {m.hours > 0 ? (
                        <button type="button" className="link-button" onClick={() => open({ kind: 'time', month: m.month, title: `Time logged — ${periodLabel(m.month)}` })}>
                          {num(m.hours, 1)}
                        </button>
                      ) : (
                        num(m.hours, 1)
                      )}
                    </td>
                    <td className="num">
                      {m.billable > 0 ? (
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => open({ kind: 'time', month: m.month, category: 'billable', title: `Billable time — ${periodLabel(m.month)}` })}
                        >
                          {num(m.billable, 1)}
                        </button>
                      ) : (
                        num(m.billable, 1)
                      )}
                    </td>
                    <td className="num">
                      {m.ticketsCompleted > 0 ? (
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => open({ kind: 'completed', month: m.month, title: `Tickets completed — ${periodLabel(m.month)}` })}
                        >
                          {num(m.ticketsCompleted)}
                        </button>
                      ) : (
                        num(m.ticketsCompleted)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="card">
          <div className="card-head">
            <h3>Projects they lead</h3>
          </div>
          {(d.projects ?? []).length === 0 ? (
            <div className="card-body">
              <p className="muted small">No open projects with them as lead.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Client</th>
                    <th>Status</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.projects ?? []).map((p) => (
                    <tr key={`${p.name}-${p.client}`}>
                      <td>{p.name}</td>
                      <td>{p.client ?? '—'}</td>
                      <td>{p.status}</td>
                      <td className="xs">{day(p.end)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {d.sources && d.sources.length > 0 && (
        <section className="card mt-4 pa-sources">
          <div className="card-head">
            <div className="grow">
              <h3>Where this data comes from</h3>
              <p className="sub">
                Everything on this tab is read live from Autotask for {day(d.from)} – {day(d.to)}. For each figure: the Autotask entity, the fields
                read, the filter, how it is worked out, and how many records it used.
              </p>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table" data-nosort>
              <thead>
                <tr>
                  <th>Figure</th>
                  <th>Autotask entity</th>
                  <th>Fields</th>
                  <th>Filter</th>
                  <th>How it is worked out</th>
                  <th className="num">Records</th>
                </tr>
              </thead>
              <tbody>
                {d.sources.map((s) => (
                  <tr key={s.figure}>
                    <td className="strong">{s.figure}</td>
                    <td className="mono xs">{s.entity}</td>
                    <td className="mono xs">{s.fields}</td>
                    <td className="xs">{s.filter}</td>
                    <td className="xs">{s.formula}</td>
                    <td className="num">{num(s.records)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {drill && <DrillDrawer employeeId={employeeId} drill={drill} name={d.name} range={{ from, to }} onClose={() => setDrill(null)} />}
    </div>
  );
}

interface Drill {
  kind: 'time' | 'completed' | 'open' | 'surveys' | 'projects' | 'tasks';
  title: string;
  month?: string;
  client?: string;
  category?: 'ticket' | 'project' | 'other' | 'billable';
  issueType?: string;
}

/** A figure as a button: the tile opens the records behind it. */
function Tile({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" className="pa-tile" onClick={onClick} title="Open the records behind this figure">
      {children}
    </button>
  );
}

type Rec = Record<string, unknown>;
const COLUMNS: Record<Drill['kind'], { key: string; label: string; num?: boolean; render?: (r: Rec) => React.ReactNode }[]> = {
  time: [
    { key: 'date', label: 'Date', render: (r) => day(r.date as string | null) },
    { key: 'work', label: 'Ticket / project' },
    { key: 'client', label: 'Client' },
    { key: 'kind', label: 'Type' },
    { key: 'hours', label: 'Hours', num: true },
    { key: 'billable', label: 'Billable', num: true },
    { key: 'notes', label: 'Notes', render: (r) => <span className="xs muted pa-notes" title={String(r.notes ?? '')}>{String(r.notes ?? '')}</span> },
  ],
  completed: [
    { key: 'completed', label: 'Completed', render: (r) => day(r.completed as string | null) },
    { key: 'number', label: 'Ticket', render: (r) => <span className="mono xs">{String(r.number)}</span> },
    { key: 'title', label: 'Title' },
    { key: 'client', label: 'Client' },
    { key: 'issueType', label: 'Issue type' },
    { key: 'priority', label: 'Priority' },
    { key: 'hours', label: 'Their hours', num: true },
  ],
  open: [
    { key: 'number', label: 'Ticket', render: (r) => <span className="mono xs">{String(r.number)}</span> },
    { key: 'title', label: 'Title' },
    { key: 'client', label: 'Client' },
    { key: 'status', label: 'Status' },
    { key: 'created', label: 'Created', render: (r) => day(r.created as string | null) },
    { key: 'ageDays', label: 'Age (days)', num: true },
  ],
  surveys: [
    { key: 'date', label: 'Date', render: (r) => day(r.date as string | null) },
    { key: 'number', label: 'Ticket', render: (r) => <span className="mono xs">{String(r.number ?? '—')}</span> },
    { key: 'title', label: 'Title' },
    { key: 'resourceRating', label: 'Their rating', num: true },
    { key: 'surveyRating', label: 'Survey rating', num: true },
  ],
  projects: [
    { key: 'name', label: 'Project' },
    { key: 'client', label: 'Client' },
    { key: 'status', label: 'Status' },
    { key: 'end', label: 'Due', render: (r) => day(r.end as string | null) },
    { key: 'completed', label: 'Completed', render: (r) => day(r.completed as string | null) },
  ],
  tasks: [
    { key: 'completed', label: 'Completed', render: (r) => day(r.completed as string | null) },
    { key: 'title', label: 'Task' },
    { key: 'project', label: 'Project' },
  ],
};

/** The records behind one figure, from the same Autotask read as the tab. */
function DrillDrawer({
  employeeId,
  drill,
  name,
  range,
  onClose,
}: {
  employeeId: number;
  drill: Drill;
  name: string;
  range: { from: string; to: string };
  onClose: () => void;
}) {
  const params = new URLSearchParams({ kind: drill.kind, from: range.from, to: range.to });
  if (drill.month) params.set('month', drill.month);
  if (drill.client) params.set('client', drill.client);
  if (drill.category) params.set('category', drill.category);
  if (drill.issueType) params.set('issueType', drill.issueType);
  const data = useResource<{ kind: string; rows: Rec[] }>(`/people/directory/${employeeId}/activity/detail?${params}`);
  const rows = data.data?.rows ?? [];
  const cols = COLUMNS[drill.kind];
  const sum = (k: string) => rows.reduce((t, r) => t + (Number(r[k]) || 0), 0);
  const exportCsv = () =>
    downloadCsv(`${slugForFile(name)}_${drill.kind}_${fileStamp()}.csv`, [cols.map((c) => c.label), ...rows.map((r) => cols.map((c) => r[c.key] as string | number | null))]);
  return (
    <Drawer open wide onClose={onClose} title={drill.title} subtitle={name}>
      {data.loading && !data.data && <Loading label="Opening the records" />}
      {data.error && <ErrorBanner message={data.error} />}
      {data.data && (
        <>
          <div className="row-between wrap mb-3">
            <p className="small">
              <strong>{num(rows.length)}</strong> record{rows.length === 1 ? '' : 's'}
              {drill.kind === 'time' && ` · ${h(sum('hours'))} · ${h(sum('billable'))} billable`}
              {drill.kind === 'completed' && ` · ${h(sum('hours'))} of their time on them`}
            </p>
            <button type="button" className="btn btn-secondary btn-sm" disabled={rows.length === 0} onClick={exportCsv}>
              Export CSV
            </button>
          </div>
          {rows.length === 0 ? (
            <p className="muted small">Nothing here.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    {cols.map((c) => (
                      <th key={c.key} className={c.num ? 'num' : undefined}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 1500).map((r, i) => (
                    <tr key={String(r.id ?? i)}>
                      {cols.map((c) => (
                        <td key={c.key} className={c.num ? 'num' : undefined}>
                          {c.render ? c.render(r) : r[c.key] === null || r[c.key] === undefined || r[c.key] === '' ? '—' : String(r[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 1500 && <p className="xs muted mt-2">Showing 1,500 of {num(rows.length)}; Export CSV has them all.</p>}
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}
